/**
 * Revenue tab (wave 2): the client's own revenue dashboard, rebuilt on the
 * synced invoice lines.
 *
 * Every number on the tab comes out of this module, so the tiles, tables and
 * drills cannot disagree with one another. revenueTabData.ts only reads rows;
 * RevenueTabWidget.tsx only draws what comes back from here.
 *
 * THE BASIS. Revenue is EARNED: a line counts in the month of its Navusoft
 * Earned Period (invoice.revenue_recognition_date), whenever it was billed
 * (AJ ruling, 24 Sep 2026). Every view reads closed months only; a month that
 * has not ended is never compared against one that has (MER-553 / MER-693).
 *
 * THE LINE TEXT. The client's tool classifies each line by its Description,
 * which Navusoft fills with the surcharge name on a surcharge line and the
 * charge code name (else the service code name) on every other line. The
 * synced invoice keeps all three (surcharge_name since MER-855), so
 * lineDescription() rebuilds it exactly.
 *
 * THE RULES ARE THE CUSTOMER'S. Which descriptions are a haul charge, a fuel
 * surcharge or core service; which account class is residential; which
 * surcharge names are fuel; which negative lines are not credits; how working
 * days are counted: all of it arrives from the customer's own files through
 * resolve-manifest.mjs (MER-819). Nothing here names one hauler's charge
 * codes. A rule the customer has not stated leaves its view awaiting.
 *
 * RATIOS ARE SUM OVER SUM (MER-715): a share or a per-haul figure divides one
 * summed quantity by another over the same lines, never an average of ratios.
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Display names for the canonical line of business ids. */
export const LINE_LABELS = Object.freeze({
  front_load: "Front Load",
  roll_off: "Roll Off",
  asl: "Residential (Side Load)",
  rl: "Residential (Rear Load)",
  hook_lift: "Hook Lift",
  tractor_trailer: "Tractor Trailer",
  compactor: "Compactor",
  portable: "Portable",
  misc: "Misc",
});

/** The key a line with no recognised line of business is shown under. */
export const MISC_LINE = "misc";

/** Categories the summary panels read, by the glossary id a rule maps to. */
export const HAUL_PARTS = Object.freeze([
  "haul_charge_revenue",
  "disposal_charge_revenue",
  "overage_charge_revenue",
]);

// ---------------------------------------------------------------------------
// Months
// ---------------------------------------------------------------------------

/** YYYY-MM from a date string; null when unparseable. */
export function monthKey(date) {
  if (typeof date !== "string") return null;
  const m = /^(\d{4})-(\d{2})/.exec(date.trim());
  return m ? `${m[1]}-${m[2]}` : null;
}

/** "Aug 2026" from "2026-08". */
export function monthLabel(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk ?? "");
  if (!m) return String(mk ?? "");
  return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/** The month before a YYYY-MM key. */
export function previousMonth(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk ?? "");
  if (!m) return null;
  let y = Number(m[1]);
  let mo = Number(m[2]) - 1;
  if (mo === 0) {
    mo = 12;
    y -= 1;
  }
  return `${y}-${String(mo).padStart(2, "0")}`;
}

/** The month `today` (a Date) falls in, YYYY-MM. */
export function currentMonth(today) {
  const d = today instanceof Date ? today : new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** The last month that has ended before `today`. */
export function lastCompleteMonth(today) {
  return previousMonth(currentMonth(today));
}

/** First day of the month `today` falls in, YYYY-MM-DD (the read's upper bound). */
export function currentMonthStart(today) {
  return `${currentMonth(today)}-01`;
}

/** True when `mk` is a month that has ended before `today`. */
export function isCompleteMonth(mk, today) {
  if (typeof mk !== "string" || !/^\d{4}-\d{2}$/.test(mk)) return false;
  const last = lastCompleteMonth(today);
  return last !== null && mk <= last;
}

/**
 * The month a single-month view shows: the page's month when it has ended,
 * else the newest ended month that has data, else the last ended month.
 */
export function selectedMonth(explicitMonth, months, today) {
  if (isCompleteMonth(explicitMonth, today)) return explicitMonth;
  const done = (months ?? []).filter((m) => isCompleteMonth(m, today));
  if (done.length > 0) return done[done.length - 1];
  return lastCompleteMonth(today);
}

/** Drop every record in a month that has not ended (MER-553 / MER-693). */
export function completeMonthsOnly(records, today) {
  const last = lastCompleteMonth(today);
  if (last === null) return [];
  return records.filter((r) => r.mk <= last);
}

/**
 * Monday to Friday in a month, holidays not removed. The working-day rule a
 * customer declares as `weekdays` (the client tool's countWeekdays).
 */
export function countWeekdays(mk) {
  const m = /^(\d{4})-(\d{2})$/.exec(mk ?? "");
  if (!m) return 0;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  let n = 0;
  for (let d = 1; d <= days; d += 1) {
    const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
    if (dow >= 1 && dow <= 5) n += 1;
  }
  return n;
}

/** Working days in a month under the customer's rule; null when it has none. */
export function workingDays(mk, rule) {
  if (rule === "weekdays") return countWeekdays(mk);
  return null;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** The line of business key a stored value is shown under. */
export function lineKey(value) {
  const s = String(value ?? "").trim();
  if (!s || s === "unknown") return MISC_LINE;
  return s;
}

/** Display name for a line key. */
export function lineLabel(key) {
  if (LINE_LABELS[key]) return LINE_LABELS[key];
  return titleCase(key);
}

function titleCase(s) {
  return String(s ?? "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

/** Division slug for comparison with the page chip ("North Side" -> "north_side"). */
export function divisionSlug(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

/**
 * Display name for a stored division: the customer's division_labels entry
 * for its id when one exists, else the stored text when it is already cased,
 * else the id title-cased.
 */
export function divisionLabel(value, labels = {}) {
  const s = String(value ?? "").trim();
  if (!s) return "Unassigned";
  const mapped = labels?.[divisionSlug(s)];
  if (typeof mapped === "string" && mapped) return mapped;
  if (/[A-Z]/.test(s)) return s;
  return titleCase(s);
}

/**
 * The line's Description as Navusoft exports it: the surcharge name on a
 * surcharge line, else the charge code name, else the service code name. On a
 * surcharge line the charge code is the PARENT charge the surcharge rides on
 * (MER-647), so it is never read there.
 */
export function lineDescription(row) {
  if (!row) return "";
  if (row.ar_type === "Surcharge") {
    return typeof row.surcharge_name === "string" ? row.surcharge_name : "";
  }
  const cc = typeof row.charge_code === "string" ? row.charge_code : "";
  if (cc) return cc;
  return typeof row.service_code === "string" ? row.service_code : "";
}

// ---------------------------------------------------------------------------
// Customer rules
// ---------------------------------------------------------------------------

function strList(v) {
  return Array.isArray(v) ? v.filter((x) => typeof x === "string" && x.length > 0) : [];
}

function normalizeRule(raw) {
  if (!raw || typeof raw !== "object") return null;
  const category = typeof raw.category === "string" ? raw.category : "";
  const mapsTo = typeof raw.maps_to === "string" ? raw.maps_to : "";
  if (!category || !mapsTo) return null;
  return {
    category,
    maps_to: mapsTo,
    equals_any: strList(raw.equals_any),
    contains: typeof raw.contains === "string" && raw.contains ? raw.contains : null,
    not_contains:
      typeof raw.not_contains === "string" && raw.not_contains ? raw.not_contains : null,
    otherwise: raw.otherwise === true,
  };
}

/**
 * One category group (roll_off / front_load / residential) from the resolved
 * config. Null when the group is not usable: every group must end in an
 * `otherwise` rule so every line lands somewhere.
 */
export function normalizeGroup(raw) {
  if (!raw || typeof raw !== "object") return null;
  const field = raw.population_field;
  const value = raw.population_value;
  if ((field !== "line_of_business" && field !== "account_class") || typeof value !== "string") {
    return null;
  }
  const rules = (Array.isArray(raw.rules) ? raw.rules : []).map(normalizeRule).filter(Boolean);
  if (rules.length === 0 || !rules[rules.length - 1].otherwise) return null;
  return { name: String(raw.name ?? ""), population_field: field, population_value: value, rules };
}

/** Does a rule match a line's text (the client's own semantics: exact, case-sensitive). */
export function ruleMatches(rule, text) {
  if (rule.otherwise) return true;
  if (rule.equals_any.length > 0) return rule.equals_any.includes(text);
  if (rule.contains === null || !text.includes(rule.contains)) return false;
  return rule.not_contains === null || !text.includes(rule.not_contains);
}

/** The first rule of a group a line's text matches. */
export function classify(group, text) {
  for (const rule of group.rules) if (ruleMatches(rule, text)) return rule;
  return group.rules[group.rules.length - 1];
}

/** The group's category names in rule order, each once. */
export function groupCategories(group) {
  const seen = [];
  for (const r of group.rules) if (!seen.includes(r.category)) seen.push(r.category);
  return seen;
}

/** Whether a record sits in a group's population. */
export function inPopulation(group, record) {
  if (group.population_field === "account_class") {
    return record.accountClass === group.population_value;
  }
  return record.lob === group.population_value;
}

/**
 * The resolved `revenue_tab` block (resolve-manifest.mjs applyRevenueTabConfig)
 * in the shape the views read. Missing pieces stay null or empty; each view
 * that needs one says so rather than borrowing a default.
 */
export function normalizeRevenueConfig(raw) {
  const cfg = raw && typeof raw === "object" ? raw : {};
  const groups = {};
  for (const [name, g] of Object.entries(cfg.groups ?? {})) {
    const group = normalizeGroup({ ...g, name });
    if (group) groups[name] = group;
  }
  return {
    view: typeof cfg.view === "string" ? cfg.view : "",
    group: typeof cfg.group === "string" ? cfg.group : null,
    groups,
    fuel_surcharge_names: strList(cfg.fuel_surcharge_names),
    // Pricing is never in the manifest (a public file): the proposal rule is
    // read at runtime from the tenant's private rate inputs, fscProposalFromRates.
    fsc_proposal: null,
    working_days: cfg.working_days === "weekdays" ? "weekdays" : null,
    credit_exclusions: strList(cfg.credit_exclusions).map((s) => s.toLowerCase()),
    division_labels:
      cfg.division_labels && typeof cfg.division_labels === "object" ? cfg.division_labels : {},
  };
}

/**
 * A negative line is a credit unless its description contains one of the
 * customer's exclusions, case-insensitively (AJ ruling, 24 Sep 2026: every
 * negative line except dry runs).
 */
export function isCreditExcluded(description, exclusions) {
  const d = String(description ?? "").toLowerCase();
  return (exclusions ?? []).some((x) => x && d.includes(x));
}

// ---------------------------------------------------------------------------
// Records
// ---------------------------------------------------------------------------

function num(v) {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** The grouping key of one grouped invoice row (the read's group columns). */
export function lineGroupKey(row) {
  return [
    row.revenue_recognition_date ?? "",
    row.division ?? "",
    row.line_of_business ?? "",
    row.ar_type ?? "",
    row.surcharge_name ?? "",
    row.charge_code ?? "",
    row.service_code ?? "",
    row.account_class ?? "",
  ].join("\u0001");
}

/**
 * Line records from the two grouped reads: every line, and the negative lines
 * only. One record per (month, division, line, account class, description);
 * `amount` is the net, `lines` the line count, `negative` every negative line,
 * `credit` the negative lines that count as credits.
 *
 * @param {Array<object>} allRows   grouped rows with `sum` and `count`
 * @param {Array<object>} negRows   the same grouping over lines with amount < 0
 * @param {{credit_exclusions: string[], division_labels?: object}} cfg
 */
export function buildLineRecords(allRows, negRows, cfg) {
  const byKey = new Map();
  const labels = cfg?.division_labels ?? {};
  const exclusions = cfg?.credit_exclusions ?? [];
  const touch = (row) => {
    const mk = monthKey(row.revenue_recognition_date);
    if (!mk) return null;
    const desc = lineDescription(row);
    const lob = lineKey(row.line_of_business);
    const accountClass = typeof row.account_class === "string" ? row.account_class : "";
    const key = [mk, row.division ?? "", lob, accountClass, desc].join("\u0001");
    let rec = byKey.get(key);
    if (!rec) {
      rec = {
        mk,
        div: divisionLabel(row.division, labels),
        divSlug: divisionSlug(row.division),
        lob,
        accountClass,
        desc,
        amount: 0,
        lines: 0,
        negative: 0,
        credit: 0,
      };
      byKey.set(key, rec);
    }
    return rec;
  };
  for (const row of allRows ?? []) {
    const rec = touch(row);
    if (!rec) continue;
    rec.amount += num(row.sum);
    rec.lines += num(row.count);
  }
  for (const row of negRows ?? []) {
    const rec = touch(row);
    if (!rec) continue;
    const neg = num(row.sum);
    rec.negative += neg;
    if (!isCreditExcluded(rec.desc, exclusions)) rec.credit += neg;
  }
  return [...byKey.values()];
}

/** Keep the records in the page's divisions (all when none picked). */
export function filterDivisions(records, divisionSlugs) {
  const wanted = new Set((divisionSlugs ?? []).map(divisionSlug).filter(Boolean));
  if (wanted.size === 0) return records;
  return records.filter((r) => wanted.has(r.divSlug));
}

/** Keep the records on the page's service lines (all when none picked). */
export function filterServices(records, services) {
  const wanted = new Set((services ?? []).filter(Boolean));
  if (wanted.size === 0) return records;
  return records.filter((r) => wanted.has(r.lob));
}

/** Sorted distinct month keys. */
export function monthsOf(records) {
  return [...new Set(records.map((r) => r.mk))].sort();
}

function sumBy(records, keyFn, valueFn = (r) => r.amount) {
  const out = new Map();
  for (const r of records) {
    const k = keyFn(r);
    out.set(k, (out.get(k) ?? 0) + valueFn(r));
  }
  return out;
}

function total(records, valueFn = (r) => r.amount) {
  let t = 0;
  for (const r of records) t += valueFn(r);
  return t;
}

/** Share, or null when the denominator cannot carry it. */
export function share(part, whole) {
  if (!Number.isFinite(part) || !Number.isFinite(whole) || whole === 0) return null;
  return part / whole;
}

/** Month over month change as a fraction, or null without a usable prior. */
export function pctChange(current, prior) {
  if (!Number.isFinite(current) || !Number.isFinite(prior) || prior === 0) return null;
  return current / prior - 1;
}

/** Divisions in the records, in the order given (config), then the rest A-Z. */
export function divisionsOf(records, order = []) {
  const seen = new Map();
  for (const r of records) if (!seen.has(r.divSlug)) seen.set(r.divSlug, r.div);
  const ordered = [];
  for (const slug of (order ?? []).map(divisionSlug)) {
    if (seen.has(slug)) {
      ordered.push({ slug, label: seen.get(slug) });
      seen.delete(slug);
    }
  }
  const rest = [...seen.entries()]
    .map(([slug, label]) => ({ slug, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [...ordered, ...rest];
}

/** Lines of business in the records, Misc last. */
export function linesOf(records) {
  const keys = [...new Set(records.map((r) => r.lob))];
  return keys.sort((a, b) => {
    if (a === MISC_LINE) return 1;
    if (b === MISC_LINE) return -1;
    return lineLabel(a).localeCompare(lineLabel(b));
  });
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

/**
 * The overview panel: all-time total, the selected month with its change on
 * the month before, the monthly average, the top division and line, and the
 * partial-month check (the newest month under 60% of the average of the
 * months before it, the client tool's own test).
 */
export function overview(records, month) {
  const months = monthsOf(records);
  const byMonth = sumBy(records, (r) => r.mk);
  const grand = total(records);
  const latest = byMonth.get(month) ?? null;
  const priorMonth = previousMonth(month);
  const prior = priorMonth && byMonth.has(priorMonth) ? byMonth.get(priorMonth) : null;
  const byDiv = sumBy(records, (r) => r.div);
  const byLob = sumBy(records, (r) => r.lob);
  const top = (m) => {
    let best = null;
    for (const [k, v] of m) if (best === null || v > best.value) best = { key: k, value: v };
    return best;
  };
  const topDiv = top(byDiv);
  const topLob = top(byLob);
  const newest = months.length > 0 ? months[months.length - 1] : null;
  const earlier = months.slice(0, -1).map((m) => byMonth.get(m) ?? 0);
  const priorAvg = earlier.length > 0 ? earlier.reduce((a, b) => a + b, 0) / earlier.length : null;
  const newestTotal = newest ? (byMonth.get(newest) ?? 0) : null;
  return {
    months,
    grand_total: months.length > 0 ? grand : null,
    avg_monthly: months.length > 0 ? grand / months.length : null,
    month,
    latest,
    prior_month: priorMonth,
    prior,
    mom: latest !== null && prior !== null ? pctChange(latest, prior) : null,
    top_division: topDiv ? { label: topDiv.key, value: topDiv.value } : null,
    top_line: topLob
      ? { key: topLob.key, label: lineLabel(topLob.key), value: topLob.value }
      : null,
    partial_month:
      newest !== null && priorAvg !== null && newestTotal !== null && newestTotal < priorAvg * 0.6
        ? { month: newest, value: newestTotal, prior_avg: priorAvg }
        : null,
  };
}

/** Monthly totals, oldest first. */
export function monthlyTotals(records) {
  const byMonth = sumBy(records, (r) => r.mk);
  return monthsOf(records).map((mk) => ({ month: mk, value: byMonth.get(mk) ?? 0 }));
}

/** One month's revenue per division with its share of the month. */
export function divisionMonth(records, month, order = []) {
  const inMonth = records.filter((r) => r.mk === month);
  const monthTotal = total(inMonth);
  const byDiv = sumBy(inMonth, (r) => r.divSlug);
  return divisionsOf(inMonth, order).map((d) => {
    const v = byDiv.get(d.slug) ?? 0;
    return { slug: d.slug, label: d.label, value: v, share: share(v, monthTotal) };
  });
}

/**
 * Revenue by month: total, change on the month before, and one column per
 * division (the customer's own divisions, never a fixed list).
 */
export function monthTable(records, order = []) {
  const divisions = divisionsOf(records, order);
  const months = monthsOf(records);
  const rows = months.map((mk, i) => {
    const inMonth = records.filter((r) => r.mk === mk);
    const byDiv = sumBy(inMonth, (r) => r.divSlug);
    const t = total(inMonth);
    const priorTotal = i > 0 ? total(records.filter((r) => r.mk === months[i - 1])) : null;
    return {
      month: mk,
      total: t,
      mom: priorTotal !== null ? pctChange(t, priorTotal) : null,
      by_division: Object.fromEntries(divisions.map((d) => [d.slug, byDiv.get(d.slug) ?? 0])),
    };
  });
  const byDivAll = sumBy(records, (r) => r.divSlug);
  return {
    divisions,
    rows,
    totals: {
      total: total(records),
      by_division: Object.fromEntries(divisions.map((d) => [d.slug, byDivAll.get(d.slug) ?? 0])),
    },
  };
}

/** All-time revenue per division: amount, share of total, invoice lines. */
export function divisionTiles(records, order = []) {
  const grand = total(records);
  return divisionsOf(records, order).map((d) => {
    const mine = records.filter((r) => r.divSlug === d.slug);
    const v = total(mine);
    return {
      slug: d.slug,
      label: d.label,
      value: v,
      share: share(v, grand),
      lines: total(mine, (r) => r.lines),
    };
  });
}

/** All-time revenue per line of business: amount, share, invoice lines. */
export function lineTiles(records) {
  const grand = total(records);
  return linesOf(records).map((lob) => {
    const mine = records.filter((r) => r.lob === lob);
    const v = total(mine);
    return {
      key: lob,
      label: lineLabel(lob),
      value: v,
      share: share(v, grand),
      lines: total(mine, (r) => r.lines),
    };
  });
}

/**
 * One slice's months, each split by a second dimension: a division's months by
 * line of business, or a line's months by division.
 *
 * @param {'division'|'line'} kind  what the slice is
 */
export function drill(records, kind, key, order = []) {
  const mine = records.filter((r) => (kind === "division" ? r.divSlug : r.lob) === key);
  const columns =
    kind === "division"
      ? linesOf(mine).map((l) => ({ key: l, label: lineLabel(l) }))
      : divisionsOf(mine, order).map((d) => ({ key: d.slug, label: d.label }));
  const rows = [];
  for (const mk of monthsOf(mine)) {
    const inMonth = mine.filter((r) => r.mk === mk);
    const t = total(inMonth);
    if (t === 0) continue;
    const by = sumBy(inMonth, (r) => (kind === "division" ? r.lob : r.divSlug));
    rows.push({
      month: mk,
      total: t,
      parts: Object.fromEntries(columns.map((c) => [c.key, by.get(c.key) ?? 0])),
    });
  }
  return { columns, rows, total: total(mine) };
}

/** Monthly revenue per division, one series each, for the trend chart. */
export function divisionSeries(records, order = []) {
  const months = monthsOf(records);
  return {
    months,
    series: divisionsOf(records, order).map((d) => {
      const by = sumBy(
        records.filter((r) => r.divSlug === d.slug),
        (r) => r.mk,
      );
      return { key: d.slug, label: d.label, values: months.map((m) => by.get(m) ?? 0) };
    }),
  };
}

// ---------------------------------------------------------------------------
// Credits
// ---------------------------------------------------------------------------

/** Credits and gross (net minus credits) for a set of records. */
export function creditTotals(records) {
  const net = total(records);
  const credits = total(records, (r) => r.credit);
  const gross = net - credits;
  return {
    net,
    credits,
    gross,
    // The client's own display rule: under a dollar of gross, no percentage.
    pct: Math.abs(gross) < 1 ? null : Math.abs(credits) / gross,
  };
}

/** Credits per division or per line, for one month or all months (null). */
export function creditsBy(records, kind, month = null, order = []) {
  const scoped = month ? records.filter((r) => r.mk === month) : records;
  const keys =
    kind === "division"
      ? divisionsOf(records, order).map((d) => ({ key: d.slug, label: d.label }))
      : linesOf(records).map((l) => ({ key: l, label: lineLabel(l) }));
  return keys.map((k) => ({
    ...k,
    ...creditTotals(scoped.filter((r) => (kind === "division" ? r.divSlug : r.lob) === k.key)),
  }));
}

/** Per month: gross, credits, net and credits as a share of gross. */
export function creditsMonthly(records) {
  return monthsOf(records).map((mk) => ({
    month: mk,
    ...creditTotals(records.filter((r) => r.mk === mk)),
  }));
}

/** The credits headline: all months, the selected month, share of gross. */
export function creditsSummary(records, month) {
  const all = creditTotals(records);
  const inMonth = creditTotals(records.filter((r) => r.mk === month));
  return {
    total_credits: all.credits,
    month,
    month_credits: inMonth.credits,
    pct_of_gross: all.pct,
  };
}

/** Revenue on lines with no recognised line of business, month x division. */
export function miscByDivision(records, order = []) {
  const misc = records.filter((r) => r.lob === MISC_LINE);
  const divisions = divisionsOf(records, order);
  const rows = [];
  for (const mk of monthsOf(misc)) {
    const inMonth = misc.filter((r) => r.mk === mk);
    const by = sumBy(inMonth, (r) => r.divSlug);
    rows.push({
      month: mk,
      total: total(inMonth),
      by_division: Object.fromEntries(divisions.map((d) => [d.slug, by.get(d.slug) ?? 0])),
    });
  }
  const byAll = sumBy(misc, (r) => r.divSlug);
  return {
    divisions,
    rows,
    totals: {
      total: total(misc),
      by_division: Object.fromEntries(divisions.map((d) => [d.slug, byAll.get(d.slug) ?? 0])),
    },
  };
}

/** Month x division x line amounts, the All Data table. */
export function allData(records, opts = {}) {
  const { division = "", line = "", month = "", search = "" } = opts;
  const q = String(search).toLowerCase();
  const by = new Map();
  for (const r of records) {
    if (division && r.divSlug !== division) continue;
    if (line && r.lob !== line) continue;
    if (month && r.mk !== month) continue;
    const label = lineLabel(r.lob);
    if (q && !(r.div.toLowerCase().includes(q) || label.toLowerCase().includes(q))) continue;
    const key = `${r.mk}\u0001${r.divSlug}\u0001${r.lob}`;
    const row = by.get(key) ?? { month: r.mk, division: r.div, line: label, amount: 0 };
    row.amount += r.amount;
    by.set(key, row);
  }
  return [...by.values()].sort(
    (a, b) =>
      a.month.localeCompare(b.month) ||
      a.division.localeCompare(b.division) ||
      a.line.localeCompare(b.line),
  );
}

// ---------------------------------------------------------------------------
// By Code (the client's YTD revenue review by code)
// ---------------------------------------------------------------------------

/** The name a line with no description is shown under. */
export const BLANK_CODE = "(Blank code)";
/** The category of a line whose description is one of the customer's fuel surcharge names. */
export const FUEL_CATEGORY = "Fuel Surcharge";
/** The category of a line with no recognised line of business. */
export const OTHER_CATEGORY = "Other / Adjustments";

/**
 * A line's code: its description (lineDescription) with surrounding spaces
 * trimmed, so "Fuel Surcharge" and "Fuel Surcharge " are one code; a blank
 * description is BLANK_CODE.
 */
export function codeName(desc) {
  const s = String(desc ?? "").trim();
  return s || BLANK_CODE;
}

/**
 * A line's category: Fuel Surcharge when its code is one of the customer's
 * fuel surcharge names, Other / Adjustments when it has no line of business,
 * else its line of business label.
 *
 * @param {{lob: string, desc: string}} record
 * @param {Set<string>} fuelNames  the customer's fuel surcharge names, trimmed
 */
export function codeCategory(record, fuelNames) {
  if (fuelNames && fuelNames.has(codeName(record.desc))) return FUEL_CATEGORY;
  if (record.lob === MISC_LINE) return OTHER_CATEGORY;
  return lineLabel(record.lob);
}

/** "YYYY-MM" months from `from` through `to`, inclusive. */
function monthRange(from, to) {
  const out = [];
  let m = from;
  while (m && m <= to && out.length < 240) {
    out.push(m);
    const [y, mo] = m.split("-").map(Number);
    m = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
  }
  return out;
}

/** The three month keys of quarter q (1 to 4) of a year. */
function quarterMonths(year, q) {
  return [1, 2, 3].map((i) => `${year}-${String((q - 1) * 3 + i).padStart(2, "0")}`);
}

/**
 * Change as a fraction of the base's size: (current - base) / |base|, so a
 * smaller credit reads as growth and a bigger one as decline (the client's
 * own rule on a negative base). Null when the base is zero.
 */
export function changeShare(current, base) {
  if (!Number.isFinite(current) || !Number.isFinite(base) || base === 0) return null;
  return (current - base) / Math.abs(base);
}

/**
 * The By Code year from the sorted closed months loaded (non-empty): the year
 * the newest falls in, its months from January (or the first loaded, if
 * later) through the newest, and the month before the newest when loaded.
 */
function codeYear(loaded) {
  const oldest = loaded[0];
  const latest = loaded[loaded.length - 1];
  const year = latest.slice(0, 4);
  const months = monthRange(oldest > `${year}-01` ? oldest : `${year}-01`, latest);
  const priorKey = previousMonth(latest);
  const prior = priorKey && priorKey >= oldest ? priorKey : null;
  return { oldest, latest, year, months, prior };
}

/**
 * The year to date by code, on the tab's earned basis, closed months only.
 *
 * The year is the one the newest closed month falls in; its months run from
 * January (or the tenant's first month with lines, if later) through that
 * month. The comparisons, generalised from the client's workbook:
 *   - the latest closed month against the month before it (which may sit in
 *     the year before);
 *   - the latest closed quarter's monthly average against Q1 of the same
 *     year. Both quarters must be fully loaded and the latest must be after
 *     Q1, else the comparison is null;
 *   - each code's share of the year to date, and the top two codes' share.
 * A quarter's monthly average is its sum over its three months, the same for
 * every code, so a code with no lines in a month counts a zero there.
 *
 * Every figure is a sum of line amounts or one sum divided by another
 * (MER-715). A code billed on more than one line of business is listed once,
 * under the category holding most of its dollars (by size) in the months
 * read, and `categories` lists its split; the category table counts every
 * line under its own category, so it ties to the tab's line of business
 * totals.
 *
 * `division` and `line` (a division slug, a line key; empty for all) narrow
 * the lines read before anything is summed (byCodeLines), so every figure,
 * share and ranking is the filtered lines' own and a share of YTD is of the
 * filtered total. The months stay every line's (the year, the latest closed
 * month, the quarters), so the columns hold under a filter and a division
 * with no lines in a month shows a zero there; on lines that span the same
 * months the view is exactly revenueByCode of the pre-filtered lines.
 *
 * @param {Array<object>} records  buildLineRecords output (canonical line keys)
 * @param {{today?: Date, fuelNames?: string[], division?: string, line?: string, growth?: number, shrink?: number, movers?: number}} [opts]
 */
export function revenueByCode(records, opts = {}) {
  const today = opts.today instanceof Date ? opts.today : new Date();
  const fuel = new Set((opts.fuelNames ?? []).map((n) => String(n).trim()).filter(Boolean));
  const closed = completeMonthsOnly(records ?? [], today);
  const loaded = monthsOf(closed);
  const population = byCodeLines(closed, opts);
  const empty = {
    year: null,
    months: [],
    latest: null,
    prior: null,
    q1: null,
    qn: null,
    totals: null,
    rows: [],
    categories: [],
    growth: [],
    shrink: [],
    increases: [],
    decreases: [],
    summary: null,
  };
  if (loaded.length === 0) return empty;
  const { oldest, latest, year, months, prior } = codeYear(loaded);

  // Quarters: Q1 of the year, and the newest quarter that has closed.
  const latestMonthNo = Number(latest.slice(5, 7));
  const qnNo = Math.floor(latestMonthNo / 3);
  const fullyLoaded = (ms) => ms.every((m) => m >= oldest && m <= latest);
  const q1Months = quarterMonths(year, 1);
  const qnMonths = qnNo >= 2 ? quarterMonths(year, qnNo) : null;
  const compare = qnMonths !== null && fullyLoaded(q1Months) && fullyLoaded(qnMonths);
  const q1 = compare ? { label: `Q1 ${year}`, months: q1Months } : null;
  const qn = compare ? { label: `Q${qnNo} ${year}`, months: qnMonths } : null;

  // The months read: the year's, plus the month before the latest when that
  // falls in the year before (a January latest month).
  const wanted = new Set([...months, ...(prior ? [prior] : [])]);
  const codes = new Map();
  const cats = new Map();
  const add = (map, key, mk, v) => {
    let e = map.get(key);
    if (!e) {
      e = { by: new Map(), split: new Map() };
      map.set(key, e);
    }
    e.by.set(mk, (e.by.get(mk) ?? 0) + v);
    return e;
  };
  for (const r of population) {
    if (!wanted.has(r.mk)) continue;
    const category = codeCategory(r, fuel);
    const e = add(codes, codeName(r.desc), r.mk, r.amount);
    e.split.set(category, (e.split.get(category) ?? 0) + r.amount);
    add(cats, category, r.mk, r.amount);
  }

  const metrics = (by) => {
    const get = (m) => by.get(m) ?? 0;
    const ytd = months.reduce((s, m) => s + get(m), 0);
    const avg = (ms) => (ms ? ms.reduce((s, m) => s + get(m), 0) / ms.length : null);
    const q1Avg = compare ? avg(q1Months) : null;
    const qnAvg = compare ? avg(qnMonths) : null;
    const cur = get(latest);
    const prev = prior ? get(prior) : null;
    return {
      by_month: Object.fromEntries(months.map((m) => [m, get(m)])),
      ytd,
      q1_avg: q1Avg,
      qn_avg: qnAvg,
      q_change: compare ? qnAvg - q1Avg : null,
      q_pct: compare ? changeShare(qnAvg, q1Avg) : null,
      latest: cur,
      prior: prev,
      m_change: prev !== null ? cur - prev : null,
      m_pct: prev !== null ? changeShare(cur, prev) : null,
    };
  };

  const allBy = new Map();
  for (const e of codes.values()) {
    for (const [m, v] of e.by) allBy.set(m, (allBy.get(m) ?? 0) + v);
  }
  const totals = metrics(allBy);
  const withShare = (m) => ({ ...m, share: share(m.ytd, totals.ytd) });

  const rows = [];
  for (const [code, e] of codes) {
    // A code with nothing in the year (only in a prior December) is kept, as
    // zeros, so the month on month movers stay whole.
    const split = [...e.split.entries()]
      .map(([category, amount]) => ({ category, amount }))
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    rows.push({
      code,
      category: split[0].category,
      categories: split,
      ...withShare(metrics(e.by)),
    });
  }
  // A code with no net activity in any month shown is left out (the client's note 4).
  const active = rows.filter(
    (r) => months.some((m) => r.by_month[m] !== 0) || (r.prior !== null && r.prior !== 0),
  );
  active.sort((a, b) => b.ytd - a.ytd || a.code.localeCompare(b.code));

  const categories = [...cats.entries()]
    .map(([category, e]) => ({ category, ...withShare(metrics(e.by)) }))
    .filter((c) => months.some((m) => c.by_month[m] !== 0) || (c.prior !== null && c.prior !== 0))
    .sort((a, b) => {
      const rank = (c) => (c === OTHER_CATEGORY ? 2 : c === FUEL_CATEGORY ? 1 : 0);
      return rank(a.category) - rank(b.category) || b.ytd - a.ytd;
    });

  const nGrowth = opts.growth ?? 12;
  const nShrink = opts.shrink ?? 8;
  const nMovers = opts.movers ?? 8;
  const growth = compare
    ? active
        .filter((r) => r.q_change > 0)
        .sort((a, b) => b.q_change - a.q_change)
        .slice(0, nGrowth)
    : [];
  const shrink = compare
    ? active
        .filter((r) => r.q_change < 0)
        .sort((a, b) => a.q_change - b.q_change)
        .slice(0, nShrink)
    : [];
  const increases = prior
    ? active
        .filter((r) => r.m_change > 0)
        .sort((a, b) => b.m_change - a.m_change)
        .slice(0, nMovers)
    : [];
  const decreases = prior
    ? active
        .filter((r) => r.m_change < 0)
        .sort((a, b) => a.m_change - b.m_change)
        .slice(0, nMovers)
    : [];

  let peak = null;
  for (const m of months) {
    const v = totals.by_month[m];
    if (peak === null || v > peak.value) peak = { month: m, value: v };
  }
  const top2 = active.slice(0, 2);
  const top2Sum = top2.reduce((s, r) => s + r.ytd, 0);

  return {
    year,
    months,
    latest,
    prior,
    q1,
    qn,
    totals: { ...totals, share: totals.ytd !== 0 ? 1 : null },
    rows: active,
    categories,
    growth,
    shrink,
    increases,
    decreases,
    summary: {
      ytd: totals.ytd,
      first_month: months[0],
      first: totals.by_month[months[0]],
      latest: totals.latest,
      prior: totals.prior,
      m_pct: totals.m_pct,
      peak,
      q1_avg: totals.q1_avg,
      qn_avg: totals.qn_avg,
      q_pct: totals.q_pct,
      top2: top2.map((r) => ({ code: r.code, ytd: r.ytd })),
      top2_share: share(top2Sum, totals.ytd),
    },
  };
}

/**
 * The By Code view's lines under its division and line of business filter: a
 * division slug and a line key, either empty for all (filterDivisions and
 * filterServices, the tab's own).
 *
 * @param {Array<object>} records
 * @param {{division?: string, line?: string}} [filter]
 */
export function byCodeLines(records, filter = {}) {
  const division = filter?.division ? [filter.division] : [];
  const line = filter?.line ? [filter.line] : [];
  return filterServices(filterDivisions(records ?? [], division), line);
}

/** The name a line of business is offered under in the By Code filter: its category's. */
function byCodeLineLabel(key) {
  return key === MISC_LINE ? OTHER_CATEGORY : lineLabel(key);
}

/**
 * The By Code filter's choices: the divisions and lines of business with
 * lines in the view's months (the year to date and the month before the
 * latest), so every choice has something behind it. Divisions in the
 * customer's division order, then A to Z, under its division labels (the
 * records carry them); lines in the customer's enabled_services order, then
 * the rest A to Z, no line of business last. A filter with one choice is not
 * a filter; the page hides it.
 *
 * @param {Array<object>} records  buildLineRecords output
 * @param {{today?: Date, order?: string[], services?: string[]}} [opts]
 */
export function byCodeOptions(records, opts = {}) {
  const today = opts.today instanceof Date ? opts.today : new Date();
  const closed = completeMonthsOnly(records ?? [], today);
  const loaded = monthsOf(closed);
  const { months, prior } = loaded.length ? codeYear(loaded) : { months: [], prior: null };
  const wanted = new Set([...months, ...(prior ? [prior] : [])]);
  const inView = closed.filter((r) => wanted.has(r.mk));
  const services = (opts.services ?? []).filter((s) => typeof s === "string" && s);
  const rank = (key) => {
    if (key === MISC_LINE) return Number.MAX_SAFE_INTEGER;
    const i = services.indexOf(key);
    return i === -1 ? services.length : i;
  };
  const lines = [...new Set(inView.map((r) => r.lob))]
    .sort((a, b) => rank(a) - rank(b) || lineLabel(a).localeCompare(lineLabel(b)))
    .map((key) => ({ key, label: byCodeLineLabel(key) }));
  return { divisions: divisionsOf(inView, opts.order ?? []), lines };
}

/**
 * The active filter in words ("North Side, Roll Off"), empty for all: the
 * view's heading, the CSV total row and the file name carry it.
 *
 * @param {{division?: string, line?: string}} filter
 * @param {{divisions: Array<{slug: string, label: string}>}} [options]  byCodeOptions output, for the labels
 */
export function byCodeFilterLabel(filter, options) {
  const parts = [];
  if (filter?.division) {
    const d = (options?.divisions ?? []).find((x) => x.slug === filter.division);
    parts.push(d ? d.label : divisionLabel(filter.division));
  }
  if (filter?.line) parts.push(byCodeLineLabel(filter.line));
  return parts.join(", ");
}

/** The filter's file name part ("_north_side_roll_off"), empty for all. */
export function byCodeFileTag(label) {
  const slug = String(label ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return slug ? `_${slug}` : "";
}

/**
 * One code's months in the year (revenueByCode's `months`), split by
 * division and by line of business: the drill behind a code row.
 */
export function codeDrill(records, code, months, order = []) {
  const want = new Set(months ?? []);
  const mine = (records ?? []).filter((r) => want.has(r.mk) && codeName(r.desc) === code);
  const divisions = divisionsOf(mine, order);
  const lines = linesOf(mine).map((l) => ({ key: l, label: lineLabel(l) }));
  const rows = [];
  for (const mk of months ?? []) {
    const inMonth = mine.filter((r) => r.mk === mk);
    if (inMonth.length === 0) continue;
    const byDiv = sumBy(inMonth, (r) => r.divSlug);
    const byLine = sumBy(inMonth, (r) => r.lob);
    rows.push({
      month: mk,
      total: total(inMonth),
      lines: total(inMonth, (r) => r.lines),
      by_division: Object.fromEntries(divisions.map((d) => [d.slug, byDiv.get(d.slug) ?? 0])),
      by_line: Object.fromEntries(lines.map((l) => [l.key, byLine.get(l.key) ?? 0])),
    });
  }
  return { divisions, lines, rows, total: total(mine), line_count: total(mine, (r) => r.lines) };
}

/**
 * The By Code table as CSV rows: the client's Data by Code columns. Under a
 * filter (byCodeFilterLabel) the total row names it: "Total (North Side)".
 */
export function byCodeCsv(view, filterLabel = "") {
  const q1 = view.q1?.label ?? "Q1";
  const qn = view.qn?.label ?? "Latest quarter";
  const latest = view.latest ? monthLabel(view.latest) : "Latest month";
  const prior = view.prior ? monthLabel(view.prior) : "prior month";
  const headers = [
    "Code",
    "Category",
    ...view.months.map(monthLabel),
    "YTD Total",
    `${q1} Avg/Mo`,
    `${qn} Avg/Mo`,
    `${qn} vs ${q1} $`,
    `${qn} vs ${q1} %`,
    `${latest} vs ${prior} $`,
    `${latest} vs ${prior} %`,
    "% of YTD",
  ];
  const line = (code, category, r) => [
    code,
    category,
    ...view.months.map((m) => r.by_month[m] ?? 0),
    r.ytd,
    r.q1_avg,
    r.qn_avg,
    r.q_change,
    r.q_pct === null ? null : r.q_pct * 100,
    r.m_change,
    r.m_pct === null ? null : r.m_pct * 100,
    r.share === null ? null : r.share * 100,
  ];
  const rows = view.rows.map((r) => line(r.code, r.category, r));
  if (view.totals) rows.push(line(csvTotalName(filterLabel), "", view.totals));
  return { headers, rows };
}

/** The total row's name in a By Code CSV: "Total", or "Total (the filter)". */
function csvTotalName(filterLabel) {
  return filterLabel ? `Total (${filterLabel})` : "Total";
}

/** The category table as CSV rows; the total row names the filter as byCodeCsv's does. */
export function byCategoryCsv(view, filterLabel = "") {
  const q1 = view.q1?.label ?? "Q1";
  const qn = view.qn?.label ?? "Latest quarter";
  const headers = [
    "Category",
    ...view.months.map(monthLabel),
    "YTD Total",
    `${q1} Avg/Mo`,
    `${qn} Avg/Mo`,
    `${qn} vs ${q1} $`,
    `${qn} vs ${q1} %`,
    "% of YTD",
  ];
  const line = (name, r) => [
    name,
    ...view.months.map((m) => r.by_month[m] ?? 0),
    r.ytd,
    r.q1_avg,
    r.qn_avg,
    r.q_change,
    r.q_pct === null ? null : r.q_pct * 100,
    r.share === null ? null : r.share * 100,
  ];
  const rows = view.categories.map((c) => line(c.category, c));
  if (view.totals) rows.push(line(csvTotalName(filterLabel), view.totals));
  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Category groups (Roll Off, Front Load, Residential)
// ---------------------------------------------------------------------------

/** The group's records, each tagged with the rule its description falls under. */
export function categoryRecords(records, group) {
  const out = [];
  for (const r of records) {
    if (!inPopulation(group, r)) continue;
    const rule = classify(group, r.desc);
    out.push({ ...r, category: rule.category, maps_to: rule.maps_to });
  }
  return out;
}

/** One month's amount and line count per glossary id and per category. */
export function categoryTotals(catRecords, month) {
  const inMonth = month ? catRecords.filter((r) => r.mk === month) : catRecords;
  const amount = {};
  const lines = {};
  const byCategory = {};
  let t = 0;
  for (const r of inMonth) {
    amount[r.maps_to] = (amount[r.maps_to] ?? 0) + r.amount;
    lines[r.maps_to] = (lines[r.maps_to] ?? 0) + r.lines;
    byCategory[r.category] = (byCategory[r.category] ?? 0) + r.amount;
    t += r.amount;
  }
  return { amount, lines, by_category: byCategory, total: t };
}

/**
 * Roll off summary: hauls are the haul charge lines (the client tool counts
 * every line in that category, credits included); haul revenue is haul plus
 * disposal plus overage; non-haul is the rest; per-haul figures divide those
 * sums by the haul lines.
 */
export function rollOffSummary(catRecords, month) {
  const t = categoryTotals(catRecords, month);
  const hauls = t.lines.haul_charge_revenue ?? 0;
  const haulRev = HAUL_PARTS.reduce((s, id) => s + (t.amount[id] ?? 0), 0);
  const nonHaul = t.amount.non_haul_revenue ?? 0;
  return {
    hauls,
    haul_revenue: haulRev,
    non_haul_revenue: nonHaul,
    total: haulRev + nonHaul,
    haul_revenue_per_haul: hauls > 0 ? haulRev / hauls : null,
    blended_revenue_per_haul: hauls > 0 ? (haulRev + nonHaul) / hauls : null,
  };
}

/** Front load summary: core, fuel surcharge, extra, and each one's share. */
export function frontLoadSummary(catRecords, month) {
  const t = categoryTotals(catRecords, month);
  const core = t.amount.core_service_revenue ?? 0;
  const fsc = t.amount.fuel_surcharge_revenue ?? 0;
  const extra = t.amount.extra_charges_revenue ?? 0;
  const tot = core + fsc + extra;
  return {
    core,
    fuel_surcharge: fsc,
    extra,
    total: tot,
    fsc_share: tot ? fsc / tot : null,
    extra_share: tot ? extra / tot : null,
  };
}

/** Residential summary: core, fees and adjustments, total, fee share. */
export function residentialSummary(catRecords, month) {
  const t = categoryTotals(catRecords, month);
  const core = t.amount.core_service_revenue ?? 0;
  const fees = t.amount.residential_fees_and_adjustments ?? 0;
  const tot = core + fees;
  return { core, fees, total: tot, fees_share: tot ? fees / tot : null };
}

/** The three descriptions with the most dollars in each category, one month. */
export function topDrivers(catRecords, group, month, n = 3) {
  const inMonth = catRecords.filter((r) => r.mk === month);
  return groupCategories(group).map((category) => {
    const by = sumBy(
      inMonth.filter((r) => r.category === category),
      (r) => r.desc,
    );
    const top = [...by.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, n)
      .map(([description, amount]) => ({ description, amount }));
    return { category, top };
  });
}

/**
 * Per month on the customer's working-day rule: the working days, and each
 * named figure divided by them. `fields` maps a column key to a function of a
 * month's summary. Null per-day values when the rule is missing.
 */
export function workingDayView(catRecords, summaryFn, fields, rule) {
  return monthsOf(catRecords).map((mk) => {
    const s = summaryFn(catRecords, mk);
    const wd = workingDays(mk, rule);
    const values = {};
    for (const [key, pick] of Object.entries(fields)) {
      const v = pick(s);
      values[key] = wd && Number.isFinite(v) ? v / wd : null;
    }
    return { month: mk, working_days: wd, summary: s, per_day: values };
  });
}

/**
 * Dollars by description and month for some categories, with category
 * subtotals: the detail tables. `negativeOnly` reads only the negative lines
 * (the client's adjustments tables, which keep dry runs in).
 */
export function breakdown(catRecords, categories, month = null, negativeOnly = false) {
  const scoped = catRecords.filter(
    (r) =>
      categories.includes(r.category) &&
      (!month || r.mk === month) &&
      (!negativeOnly || r.negative !== 0),
  );
  const months = monthsOf(scoped);
  const value = (r) => (negativeOnly ? r.negative : r.amount);
  const sections = [];
  for (const category of categories) {
    const inCat = scoped.filter((r) => r.category === category);
    if (inCat.length === 0) continue;
    const descs = [...new Set(inCat.map((r) => r.desc))].sort();
    const rows = descs.map((description) => {
      const mine = inCat.filter((r) => r.desc === description);
      const by = sumBy(mine, (r) => r.mk, value);
      return {
        description,
        by_month: Object.fromEntries(months.map((m) => [m, by.get(m) ?? 0])),
        total: total(mine, value),
      };
    });
    const sub = sumBy(inCat, (r) => r.mk, value);
    sections.push({
      category,
      rows,
      subtotal: {
        by_month: Object.fromEntries(months.map((m) => [m, sub.get(m) ?? 0])),
        total: total(inCat, value),
      },
    });
  }
  return { months, sections };
}

// ---------------------------------------------------------------------------
// Accounts
// ---------------------------------------------------------------------------

/**
 * Account records from the account-grain reads: every line grouped by
 * (account, month, division, line), and the negative lines grouped with their
 * text so the credit exclusions can be applied.
 */
export function buildAccountRecords(allRows, negRows, names, cfg) {
  const labels = cfg?.division_labels ?? {};
  const exclusions = cfg?.credit_exclusions ?? [];
  const nameOf = names instanceof Map ? names : new Map(Object.entries(names ?? {}));
  const byKey = new Map();
  const touch = (row) => {
    const mk = monthKey(row.revenue_recognition_date);
    const account = row.customer_id == null ? "" : String(row.customer_id);
    if (!mk || !account) return null;
    const lob = lineKey(row.line_of_business);
    const key = [account, mk, row.division ?? "", lob].join("\u0001");
    let rec = byKey.get(key);
    if (!rec) {
      rec = {
        account,
        name: nameOf.get(account) || account,
        mk,
        div: divisionLabel(row.division, labels),
        divSlug: divisionSlug(row.division),
        lob,
        amount: 0,
        credit: 0,
      };
      byKey.set(key, rec);
    }
    return rec;
  };
  for (const row of allRows ?? []) {
    const rec = touch(row);
    if (rec) rec.amount += num(row.sum);
  }
  for (const row of negRows ?? []) {
    const rec = touch(row);
    if (!rec) continue;
    if (!isCreditExcluded(lineDescription(row), exclusions)) rec.credit += num(row.sum);
  }
  return [...byKey.values()];
}

/**
 * One row per account: its divisions and lines, revenue, credits and months
 * active, for all months or one.
 */
export function accountsSummary(accts, month = null) {
  const by = new Map();
  for (const r of accts) {
    if (month && r.mk !== month) continue;
    let a = by.get(r.account);
    if (!a) {
      a = {
        account: r.account,
        name: r.name,
        divisions: new Set(),
        lines: new Set(),
        amount: 0,
        credits: 0,
        months: new Set(),
      };
      by.set(r.account, a);
    }
    a.divisions.add(r.div);
    a.lines.add(lineLabel(r.lob));
    a.amount += r.amount;
    a.credits += r.credit;
    a.months.add(r.mk);
  }
  return [...by.values()].map((a) => ({
    account: a.account,
    name: a.name,
    divisions: [...a.divisions].sort().join(", "),
    division_list: [...a.divisions],
    lines: [...a.lines].sort().join(", "),
    amount: a.amount,
    credits: a.credits,
    month_count: a.months.size,
  }));
}

/** Accounts behind a slice (a division or a line, optionally one month). */
export function accountsFor(accts, pred) {
  const by = new Map();
  for (const r of accts) {
    if (!pred(r)) continue;
    const a = by.get(r.account) ?? {
      account: r.account,
      name: r.name,
      amount: 0,
      credits: 0,
      months: new Set(),
    };
    a.amount += r.amount;
    a.credits += r.credit;
    a.months.add(r.mk);
    by.set(r.account, a);
  }
  return [...by.values()]
    .map((a) => ({
      account: a.account,
      name: a.name,
      amount: a.amount,
      credits: a.credits,
      month_count: a.months.size,
    }))
    .sort((a, b) => b.amount - a.amount);
}

/** The five accounts that rose most and the five that fell most, month on month. */
export function movers(accts, month, prior, n = 5) {
  const cur = new Map();
  const prev = new Map();
  const meta = new Map();
  for (const r of accts) {
    if (r.mk === month) cur.set(r.account, (cur.get(r.account) ?? 0) + r.amount);
    else if (r.mk === prior) prev.set(r.account, (prev.get(r.account) ?? 0) + r.amount);
    else continue;
    if (!meta.has(r.account) || r.mk === month) meta.set(r.account, { name: r.name, div: r.div });
  }
  const all = new Set([...cur.keys(), ...prev.keys()]);
  const moves = [...all].map((account) => {
    const c = cur.get(account) ?? 0;
    const p = prev.get(account) ?? 0;
    return { account, ...meta.get(account), current: c, prior: p, delta: c - p };
  });
  return {
    gainers: moves
      .filter((m) => m.delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, n),
    decliners: moves
      .filter((m) => m.delta < 0)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, n),
  };
}

/** The accounts with the largest credits over every month loaded. */
export function topCreditAccounts(accts, n = 10) {
  return accountsSummary(accts)
    .filter((a) => a.credits < 0)
    .sort((a, b) => a.credits - b.credits)
    .slice(0, n);
}

/** One account's monthly totals and its month x division x line rows. */
export function accountDetail(accts, account) {
  const mine = accts.filter((r) => r.account === account);
  const months = [...new Set(mine.map((r) => r.mk))].sort();
  const by = sumBy(mine, (r) => r.mk);
  return {
    months,
    monthly: months.map((m) => by.get(m) ?? 0),
    total: total(mine),
    rows: [...mine]
      .sort((a, b) => a.mk.localeCompare(b.mk) || a.div.localeCompare(b.div))
      .map((r) => ({ month: r.mk, division: r.div, line: lineLabel(r.lob), amount: r.amount })),
  };
}

// ---------------------------------------------------------------------------
// Fuel surcharge by account
// ---------------------------------------------------------------------------

/** The tenant's private rate input keys that hold its fuel surcharge proposal rule. */
export const FSC_PROPOSAL_RATE_KEYS = Object.freeze({
  floor: "fsc_proposal_floor",
  step: "fsc_proposal_step",
});

/**
 * The fuel surcharge proposal rule from the tenant's private rate inputs
 * (public.tenant_rate_input, tenant RLS; latestRates output keyed
 * `scope|scope_key|rate_key`). Company scope, unit pct, stored as percent
 * (11 = 11 percent). Null unless both keys are present and sane: a missing
 * rule leaves the proposed columns awaiting it, never a default.
 */
export function fscProposalFromRates(latest) {
  const get = (key) => {
    const row = latest instanceof Map ? latest.get(`company||${key}`) : null;
    if (!row || row.rate_unit !== "pct") return null;
    const v = Number(row.rate_value);
    return Number.isFinite(v) ? v / 100 : null;
  };
  const floor = get(FSC_PROPOSAL_RATE_KEYS.floor);
  const step = get(FSC_PROPOSAL_RATE_KEYS.step);
  if (floor === null || step === null || !(floor > 0 && floor < 1) || step < 0) return null;
  return { floor_share: floor, step_share: step };
}

/**
 * The customer's proposed fuel surcharge share for an account: its current
 * share plus the step when it is already above the floor, otherwise the
 * floor. Null when the customer has no proposal rule.
 */
export function proposedShare(current, proposal) {
  if (!proposal || current === null || !Number.isFinite(current)) return null;
  return current > proposal.floor_share ? current + proposal.step_share : proposal.floor_share;
}

/**
 * Per account for one month (or every month, month = null): fuel surcharge
 * dollars (lines whose surcharge name is one of the customer's fuel names),
 * total billed (every earned line for the account), the share, and the
 * customer's proposal: the new share J, the charge J x (billed - fuel) / (1 -
 * J) that makes fuel J of the new bill, the new bill, and the change in share
 * in percentage points. Only accounts with a fuel surcharge line are listed.
 *
 * BOTH SIDES ON ONE POPULATION (MER-715). `scope` (the page's divisions and
 * service lines) is applied here to the surcharge rows AND to the account
 * records, by the same division slug and line key, so an account's front
 * load fuel dollars are never divided by its roll off revenue alone when the
 * page is cut to one line. The surcharge rows carry line_of_business for it.
 *
 * @param {Array<{customer_id, revenue_recognition_date, division, line_of_business, surcharge_name, sum}>} fscRows
 * @param {Array} accts  buildAccountRecords output
 * @param {{divisions?: string[], services?: string[]}} [scope]
 */
export function fuelSurchargeByAccount(fscRows, allAccts, names, month, proposal, scope = {}) {
  const accts = filterServices(
    filterDivisions(allAccts, scope.divisions ?? []),
    scope.services ?? [],
  );
  const wantDiv = new Set((scope.divisions ?? []).map(divisionSlug).filter(Boolean));
  const wantLine = new Set((scope.services ?? []).filter(Boolean));
  const fuelNames = new Set(names ?? []);
  const inScope = new Set(accts.map((r) => r.account));
  const nameOf = new Map(accts.map((r) => [r.account, r.name]));
  const fsc = new Map();
  const fscMonths = new Map();
  for (const row of fscRows ?? []) {
    const account = row.customer_id == null ? "" : String(row.customer_id);
    const mk = monthKey(row.revenue_recognition_date);
    if (!account || !mk || !fuelNames.has(row.surcharge_name)) continue;
    if (month && mk !== month) continue;
    if (wantDiv.size > 0 && !wantDiv.has(divisionSlug(row.division))) continue;
    if (wantLine.size > 0 && !wantLine.has(lineKey(row.line_of_business))) continue;
    if (!inScope.has(account)) continue;
    fsc.set(account, (fsc.get(account) ?? 0) + num(row.sum));
    const ms = fscMonths.get(account) ?? new Set();
    ms.add(mk);
    fscMonths.set(account, ms);
  }
  const billed = sumBy(
    accts.filter((r) => !month || r.mk === month),
    (r) => r.account,
  );
  const rows = [];
  for (const [account, charge] of fsc) {
    const bill = billed.get(account) ?? 0;
    const current = share(charge, bill);
    const j = proposedShare(current, proposal);
    const proposedCharge = j !== null && j < 1 ? (j * (bill - charge)) / (1 - j) : null;
    rows.push({
      account,
      name: nameOf.get(account) ?? account,
      fsc: charge,
      billed: bill,
      share: current,
      proposed_share: j,
      proposed_charge: proposedCharge,
      proposed_billed: proposedCharge !== null ? bill - charge + proposedCharge : null,
      share_increase: j !== null && current !== null ? j - current : null,
      month_count: fscMonths.get(account)?.size ?? 0,
    });
  }
  return rows.sort((a, b) => b.fsc - a.fsc);
}

/** Every account's revenue over all months and in one month. */
export function accountTotals(accts, month) {
  const all = sumBy(accts, (r) => r.account);
  const inMonth = sumBy(
    accts.filter((r) => r.mk === month),
    (r) => r.account,
  );
  const nameOf = new Map(accts.map((r) => [r.account, r.name]));
  return [...all.entries()]
    .map(([account, amount]) => ({
      account,
      name: nameOf.get(account) ?? account,
      all_time: amount,
      month: inMonth.get(account) ?? 0,
    }))
    .sort((a, b) => b.all_time - a.all_time);
}

// ---------------------------------------------------------------------------
// Reading every page
// ---------------------------------------------------------------------------

/**
 * Read every page of a grouped query, several pages at a time.
 *
 * Pages are requested in waves of `concurrency`. The read ends at the first
 * page that comes back short; pages after it in the same wave are discarded.
 * Every page before the short one is full by construction, so no row is
 * skipped. If every page up to `maxPages` came back full the result is
 * flagged `truncated`, and the caller withholds the view instead of showing a
 * partial set (never silently cut).
 *
 * @param {(from: number, to: number) => PromiseLike<{data: unknown[]|null, error: {message?: string}|null}>} queryPage
 * @param {{pageSize?: number, maxPages?: number, concurrency?: number}} [opts]
 * @returns {Promise<{rows: unknown[], truncated: boolean, pages: number}>}
 */
export async function fetchPagesConcurrently(queryPage, opts = {}) {
  const pageSize = Math.max(1, Math.trunc(Number(opts.pageSize) || 1000));
  const maxPages = Math.max(1, Math.trunc(Number(opts.maxPages) || 100));
  const concurrency = Math.max(1, Math.trunc(Number(opts.concurrency) || 4));
  const rows = [];
  let requested = 0;
  for (let start = 0; start < maxPages; start += concurrency) {
    const wave = [];
    for (let p = start; p < Math.min(start + concurrency, maxPages); p += 1) {
      wave.push(queryPage(p * pageSize, (p + 1) * pageSize - 1));
    }
    requested += wave.length;
    const results = await Promise.all(wave);
    for (const res of results) {
      if (res?.error) {
        const err = new Error(res.error.message || "revenue read failed");
        throw err;
      }
      const batch = Array.isArray(res?.data) ? res.data : [];
      rows.push(...batch);
      if (batch.length < pageSize) return { rows, truncated: false, pages: requested };
    }
  }
  return { rows, truncated: true, pages: requested };
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(2)) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** A CSV document from a header row and data rows. */
export function toCsv(headers, rows) {
  return [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
}
